// src/components/AtributosSelector/AtributosSelector.js
/**
 * Componente para seleccionar tipos de atributos
 * Se usa en el formulario de edición de categorías
 * 
 * Props:
 * - atributosTiposDisponibles: Array de todos los tipos disponibles
 * - atributosTipoIdsSeleccionados: Array de IDs seleccionados actualmente
 * - onChange: Función que recibe el nuevo array de IDs seleccionados
 * - disabled: Si el componente está deshabilitado
 * - loading: Si está cargando
 */
import React from 'react'
import Form from 'react-bootstrap/Form'
import Badge from 'react-bootstrap/Badge'
import Alert from 'react-bootstrap/Alert'

import './AtributosSelector.css'

function AtributosSelector({
	atributosTiposDisponibles = [],
	atributosTipoIdsSeleccionados = [],
	onChange,
	disabled = false,
	loading = false,
	itemsAfectados = 0
}) {
	const handleToggle = (tipoId) => {
		if (disabled) return

		const nuevosIds = atributosTipoIdsSeleccionados.includes(tipoId)
			? atributosTipoIdsSeleccionados.filter(id => id !== tipoId)
			: [...atributosTipoIdsSeleccionados, tipoId]

		onChange(nuevosIds)
	}

	const tiposActivos = atributosTiposDisponibles.filter(t => t.activo)
	const tiposInactivos = atributosTiposDisponibles.filter(t => !t.activo)

	if (loading) {
		return (
			<div className="atributos-selector loading">
				<i className="fa fa-spinner fa-spin me-2"></i>
				Cargando atributos...
			</div>
		)
	}

	if (atributosTiposDisponibles.length === 0) {
		return (
			<Alert variant="info" className="mb-0">
				<i className="fa fa-info-circle me-2"></i>
				No hay tipos de atributos configurados. 
				<a href="/atributos" className="alert-link ms-1">
					Crear tipos de atributos
				</a>
			</Alert>
		)
	}

	return (
		<div className={`atributos-selector ${disabled ? 'disabled' : ''}`}>
			<p className="text-muted small mb-3">
				Seleccione los atributos que aplicarán por defecto a los productos de esta categoría.
				Los productos heredarán estos atributos automáticamente.
			</p>

			{itemsAfectados > 0 && (
				<Alert variant="warning" className="py-2 mb-3">
					<small>
						<i className="fa fa-exclamation-triangle me-1"></i>
						Esta categoría tiene <strong>{itemsAfectados}</strong> producto(s). 
						Los cambios afectarán qué atributos heredan.
					</small>
				</Alert>
			)}

			{/* Tipos activos */}
			{tiposActivos.length > 0 && (
				<div className="tipos-lista mb-3">
					{tiposActivos.map((tipo) => {
						const isSelected = atributosTipoIdsSeleccionados.includes(tipo.id)
						return (
							<div
								key={tipo.id}
								className={`tipo-item ${isSelected ? 'selected' : ''}`}
								onClick={() => handleToggle(tipo.id)}
							>
								<div className="tipo-check">
									<Form.Check
										type="checkbox"
										checked={isSelected}
										onChange={() => handleToggle(tipo.id)}
										disabled={disabled}
										id={`tipo-${tipo.id}`}
									/>
								</div>
								<div className="tipo-info">
									<div className="tipo-nombre">{tipo.nombre}</div>
									{tipo.descripcion && (
										<div className="tipo-descripcion">{tipo.descripcion}</div>
									)}
									<div className="tipo-valores">
										<small className="text-muted">
											{tipo.valores?.length || 0} valores disponibles
										</small>
									</div>
								</div>
								{isSelected && (
									<Badge bg="primary" className="ms-auto">
										<i className="fa fa-check"></i>
									</Badge>
								)}
							</div>
						)
					})}
				</div>
			)}

			{/* Tipos inactivos (si hay alguno seleccionado) */}
			{tiposInactivos.length > 0 && tiposInactivos.some(t => 
				atributosTipoIdsSeleccionados.includes(t.id)
			) && (
				<div className="tipos-inactivos mt-3">
					<small className="text-muted d-block mb-2">
						<i className="fa fa-eye-slash me-1"></i>
						Atributos inactivos (no visibles en la web):
					</small>
					{tiposInactivos
						.filter(t => atributosTipoIdsSeleccionados.includes(t.id))
						.map((tipo) => (
							<div
								key={tipo.id}
								className="tipo-item inactivo"
								onClick={() => handleToggle(tipo.id)}
							>
								<div className="tipo-check">
									<Form.Check
										type="checkbox"
										checked={true}
										onChange={() => handleToggle(tipo.id)}
										disabled={disabled}
										id={`tipo-inactivo-${tipo.id}`}
									/>
								</div>
								<div className="tipo-info">
									<div className="tipo-nombre">
										{tipo.nombre}
										<Badge bg="secondary" className="ms-2">Inactivo</Badge>
									</div>
								</div>
							</div>
						))}
				</div>
			)}

			{/* Resumen */}
			{atributosTipoIdsSeleccionados.length > 0 && (
				<div className="resumen-seleccion mt-3 pt-3 border-top">
					<small className="text-muted">
						<strong>{atributosTipoIdsSeleccionados.length}</strong> tipo(s) de atributo seleccionado(s)
					</small>
				</div>
			)}
		</div>
	)
}

export default AtributosSelector